import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  getTemplateDetailURLByHashid,
  PRODUCTBUNDLE_API_URL,
  TEMPLATES_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {
  DOCUMENT_TAG,
  MARKET_RTK_TAGS,
  POLICY_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
  PROCESS_RTK_TAGS,
  TEMPLATE_RTK_TAGS,
} from 'app/utils/rtk-tags';

import {
  BulkCreateTemplateParams,
  BulkDeleteTemplateParams,
  CreateTemplateParams,
  TemplateFetchParams,
  TemplateLocationParams,
  TransferTemplateParams,
} from './types';

import {TemplateSerializedType} from 'sweet-types/market';

import type {ToastMessageOptionsType} from 'app/toasts/slice';
import type {
  CompletedUserJobSerializedType,
  ListResponseType,
  ProductSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [MARKET_RTK_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      products: build.query<
        ListResponseType<ProductSerializedType>,
        {
          team: string;
        }
      >({
        query: (params) => ({
          url: PRODUCTBUNDLE_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(MARKET_RTK_TAGS.TAGS),
      }),
      createProduct: build.mutation<
        ProductSerializedType,
        {team: string; html_url: string}
      >({
        query: (body) => ({
          url: PRODUCTBUNDLE_API_URL,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) =>
          showMessages({
            onSuccess: ({html_url}) => {
              const segments = html_url.split('/');
              const base = segments.slice(0, -2);
              const url = `${base.join('/')}/products/?ordering=-created_at`;
              return getCreatedProductNotification(url);
            },
            onError: () => gt('Unable to create product.'),
          })(args, api),
        invalidatesTags: () => [{type: MARKET_RTK_TAGS.TAGS, id: 'list'}],
      }),
      updateProduct: build.mutation<
        ProductSerializedType,
        Pick<ProductSerializedType, 'url'> & Partial<ProductSerializedType>
      >({
        query: ({url, name, description, is_public}) => ({
          url,
          method: 'PATCH',
          body: {name, description, is_public},
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Product updated'),
          onError: () => gt('Unable to update product'),
        }),
        invalidatesTags: (product) => [
          {type: MARKET_RTK_TAGS.TAGS, id: 'list'},
          {type: MARKET_RTK_TAGS.TAGS, id: product?.url ?? ''},
        ],
      }),
      deleteProduct: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Product deleted'),
          onError: () => gt('Unable to delete product'),
        }),
        invalidatesTags: () => [{type: MARKET_RTK_TAGS.TAGS, id: 'list'}],
      }),
      templates: build.query<
        ListResponseType<TemplateSerializedType>,
        TemplateFetchParams
      >({
        query: (params) => ({
          url: TEMPLATES_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(TEMPLATE_RTK_TAGS.TAGS),
      }),
      templateByHashid: build.query<TemplateSerializedType, string>({
        query: (hashid) => ({
          url: getTemplateDetailURLByHashid(hashid),
        }),
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the template.'),
        }),
        providesTags: (response) =>
          response != null
            ? [{type: TEMPLATE_RTK_TAGS.TAGS, id: response.url}]
            : [],
      }),
      createTemplate: build.mutation<
        TemplateSerializedType,
        CreateTemplateParams
      >({
        query: (body) => ({
          url: TEMPLATES_API_URL,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) =>
          showMessages({
            onSuccess: () => gt('Template created.'),
            onError: {
              fallback: () => gt('Unable to create template.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
            },
          })(args, api),
        invalidatesTags: () => [
          {type: TEMPLATE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
        ],
      }),
      deleteTemplate: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Template deleted.'),
          onError: () => gt('Unable to delete template.'),
        }),
        invalidatesTags: () => [{type: TEMPLATE_RTK_TAGS.TAGS, id: 'list'}],
      }),
      transferTemplate: build.mutation<
        CompletedUserJobSerializedType,
        TransferTemplateParams
      >({
        queryFn: (
          {template, name},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TEMPLATES_API_URL}transfer/`,
                    method: 'POST',
                    body: {
                      template,
                      name,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [
          {type: TEMPLATE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              gt('A document based on this template is being created...'),
            onSuccess: () => [
              gt('A document based on this template has been created'),
              {level: 'success'},
            ],
            onError: () =>
              gt(
                "There was an error and we couldn't create a document based on this template."
              ),
          })(args, api);
        },
      }),
      bulkCreateTemplates: build.mutation<
        CompletedUserJobSerializedType,
        BulkCreateTemplateParams
      >({
        queryFn: (
          {templates},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TEMPLATES_API_URL}bulk_create/`,
                    method: 'POST',
                    body: {
                      templates: templates,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [
          {type: TEMPLATE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              gt('Templates are being created for the selected documents...'),
            onSuccess: () => [
              gt('Templates created for the selected documents'),
              {level: 'success'},
            ],
            onError: () =>
              gt(
                "There was an error and we couldn't create templates for the selected items."
              ),
          })(args, api);
        },
      }),
      bulkDeleteTemplates: build.mutation<
        CompletedUserJobSerializedType,
        BulkDeleteTemplateParams
      >({
        queryFn: (
          {hashids},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TEMPLATES_API_URL}bulk_delete/`,
                    method: 'POST',
                    body: {
                      hashids,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [
          {type: TEMPLATE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () => gt('The selected templates are being deleted...'),
            onSuccess: () => [
              gt('All the selected templates were deleted'),
              {level: 'success'},
            ],
            onError: () =>
              gt(
                "There was an error and we couldn't delete the selected templates."
              ),
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useCreateProductMutation: useCreateProduct,
  useProductsQuery: useProducts,
  useUpdateProductMutation: useUpdateProduct,
  useDeleteProductMutation: useDeleteProduct,
  useTemplatesQuery: useTemplates,
  useTemplateByHashidQuery: useTemplateByHashid,
  useCreateTemplateMutation: useCreateTemplate,
  useDeleteTemplateMutation: useDeleteTemplate,
  useTransferTemplateMutation: useTransferTemplate,
  useBulkCreateTemplatesMutation: useBulkCreateTemplates,
  useBulkDeleteTemplatesMutation: useBulkDeleteTemplates,
} = extendedAPI;

const getCreatedProductNotification = (
  productUrl: string
): [string, ToastMessageOptionsType] => {
  return [
    gt('Product created.'),
    {
      link: {
        text: gt('View product'),
        to: productUrl,
      },
      timeout: 8000,
    },
  ];
};

export const convertLocationParamsToFetchParams = (
  query: TemplateLocationParams
): TemplateFetchParams => {
  return {
    ...query,
    page: query.page != null ? query.page : '1',
    page_size: query.page_size ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
  };
};
